import { CountryWithMCC, useGetCountryDataQuery } from '@/src/api/rtkQuery/countryApi'
import DateSelectorInputWrapper from '@/src/components/common/itinerary/itinearyDateSelectorInput'
import ItineraryTextField from '@/src/components/common/itinerary/itinearyTextField'
import { DateFields, DocumentDetails } from '@/src/types/itinerary'
import { ValidationState } from '@/src/utils/itinerary/travelerInputValidationRules'
import { Box, InputLabel } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import React from 'react'
import ItineraryAutoComplete from '../../itinerary/auto-complete'

interface DocumentDetailsFormProps {
    documentInputs: Record<keyof DocumentDetails, any>
    selectedDocumentDetails: DocumentDetails
    validationState?: ValidationState
    fieldPrefix?: string // e.g., "traveler_0_PASSPORT" for traveler 0's passport
    onDocumentDetailsChange: <K extends keyof DocumentDetails>(field: K, value: DocumentDetails[K]) => void
    onFieldBlur?: (fieldKey: string, inputValue?: any) => void
}

const DocumentDetailsForm: React.FC<DocumentDetailsFormProps> = ({
    documentInputs,
    selectedDocumentDetails,
    validationState,
    fieldPrefix = '',
    onDocumentDetailsChange,
    onFieldBlur
}) => {
    const travelerDetailsTranslate = useTranslations('itineraryPage.travelerDetailsRecord')
    const commonTranslate = useTranslations('common')
    const { docNumber, nationality, countryIssued, expiryDate, issueDate } = documentInputs

    const locale = useLocale()
    const { data: countryData, isLoading: isCountryDataLoading } = useGetCountryDataQuery()

    const selectedCountryIssued = React.useMemo(
        () => countryData?.find(v => v.en.toLowerCase() === selectedDocumentDetails.countryIssued?.toLowerCase()),
        [countryData, selectedDocumentDetails?.countryIssued]
    )

    const selectedNationality = React.useMemo(
        () => countryData?.find(v => v.en.toLowerCase() === selectedDocumentDetails.nationality?.toLowerCase()),
        [countryData, selectedDocumentDetails?.nationality]
    )

    // Helper function to get field validation state
    const getFieldValidation = (fieldName: string) => {
        const fieldKey = fieldPrefix ? `${fieldPrefix}_${fieldName}` : fieldName
        return validationState?.[fieldKey] || { isError: false, errorMessage: '', touched: false }
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2
            }}
        >
            {/*document id /number */}
            {!!docNumber && (
                <Box sx={{ flex: 1, minWidth: 220 }}>
                    <InputLabel
                        shrink
                        // htmlFor={`passport-number-${idx}`}
                        sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}
                    >
                        {travelerDetailsTranslate(docNumber.label)}
                    </InputLabel>
                    <ItineraryTextField
                        // id={`passport-number-${idx}`}
                        placeholder={travelerDetailsTranslate(docNumber.label)}
                        value={selectedDocumentDetails?.docNumber}
                        onChange={e => {
                            onDocumentDetailsChange('docNumber', e.target.value)
                        }}
                        onBlur={e => onFieldBlur?.(`${fieldPrefix}_docNumber`, e.target.value)}
                        error={getFieldValidation('docNumber').isError && getFieldValidation('docNumber').touched}
                        helperText={
                            getFieldValidation('docNumber').touched && getFieldValidation('docNumber').errorMessage
                        }
                    />
                </Box>
            )}

            {/* Nationality */}
            {!!nationality && (
                <Box sx={{ flex: 1, minWidth: 220 }}>
                    <ItineraryAutoComplete
                        loading={isCountryDataLoading}
                        label={travelerDetailsTranslate(nationality.label)}
                        options={countryData || []}
                        groupBy={e => commonTranslate(e.category)}
                        value={selectedNationality || null}
                        onChange={value => onDocumentDetailsChange('nationality', value?.en || '')}
                        placeholder={travelerDetailsTranslate(nationality.placeholder)}
                        error={getFieldValidation('nationality').isError && getFieldValidation('nationality').touched}
                        errorMessage={getFieldValidation('nationality').errorMessage}
                        onBlur={() => onFieldBlur?.(`${fieldPrefix}_nationality`)}
                        getOptionLabel={option => option[locale as keyof CountryWithMCC]}
                        readOnly={nationality.readOnly}
                    />
                </Box>
            )}

            {/* Issued Country */}
            {!!countryIssued && (
                <Box sx={{ flex: 1, minWidth: 220 }}>
                    <ItineraryAutoComplete
                        loading={isCountryDataLoading}
                        label={travelerDetailsTranslate(countryIssued.label)}
                        groupBy={e => commonTranslate(e.category)}
                        options={countryData || []}
                        value={selectedCountryIssued || null}
                        onChange={value => onDocumentDetailsChange('countryIssued', value?.en || '')}
                        placeholder={travelerDetailsTranslate(countryIssued.placeholder)}
                        error={
                            getFieldValidation('countryIssued').isError && getFieldValidation('countryIssued').touched
                        }
                        errorMessage={getFieldValidation('countryIssued').errorMessage}
                        onBlur={() => onFieldBlur?.(`${fieldPrefix}_countryIssued`)}
                        getOptionLabel={option => option[locale as keyof CountryWithMCC]}
                        readOnly={countryIssued.readOnly}
                    />
                </Box>
            )}

            <Box
                sx={{
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                    gap: 2
                }}
            >
                {/* Expiry Date */}
                {!!expiryDate && (
                    <Box sx={{ flex: 1, minWidth: 300 }}>
                        <InputLabel shrink sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}>
                            {travelerDetailsTranslate(expiryDate.label)}
                        </InputLabel>

                        <DateSelectorInputWrapper
                            dateValue={
                                (selectedDocumentDetails?.expiryDate as DateFields) || { day: '', month: '', year: '' }
                            }
                            days={expiryDate.days}
                            months={expiryDate.months}
                            years={expiryDate.years}
                            onChange={e => {
                                onDocumentDetailsChange('expiryDate', e)
                            }}
                            onBlur={() => onFieldBlur?.(`${fieldPrefix}_expiryDate`)}
                        />
                        {getFieldValidation('expiryDate').touched && getFieldValidation('expiryDate').errorMessage && (
                            <Box sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>
                                {getFieldValidation('expiryDate').errorMessage}
                            </Box>
                        )}
                    </Box>
                )}
                {/* Issue Date */}
                {!!issueDate && (
                    <Box sx={{ flex: 1, minWidth: 300 }}>
                        <InputLabel shrink sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}>
                            {travelerDetailsTranslate(issueDate.label)}
                        </InputLabel>
                        <DateSelectorInputWrapper
                            dateValue={
                                (selectedDocumentDetails?.issueDate as DateFields) || { day: '', month: '', year: '' }
                            }
                            days={issueDate.days}
                            months={issueDate.months}
                            years={issueDate.years}
                            onChange={e => {
                                onDocumentDetailsChange('issueDate', e)
                            }}
                            onBlur={() => onFieldBlur?.(`${fieldPrefix}_issueDate`)}
                        />
                        {getFieldValidation('issueDate').touched && getFieldValidation('issueDate').errorMessage && (
                            <Box sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>
                                {getFieldValidation('issueDate').errorMessage}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

const areEqual = (prevProps: DocumentDetailsFormProps, nextProps: DocumentDetailsFormProps) => {
    if (prevProps.fieldPrefix !== nextProps.fieldPrefix) return false

    const relevantFields = (['docNumber', 'nationality', 'countryIssued', 'expiryDate', 'issueDate'] as const).filter(
        f => Boolean((prevProps.documentInputs as any)[f]) || Boolean((nextProps.documentInputs as any)[f])
    )

    // documentInputs reference change may be harmless, but if flags actually differ, re-render
    const prevFlags = relevantFields.map(f => Boolean((prevProps.documentInputs as any)[f])).join(',')
    const nextFlags = relevantFields.map(f => Boolean((nextProps.documentInputs as any)[f])).join(',')
    if (prevFlags !== nextFlags) return false

    for (const f of relevantFields) {
        const prevVal = (prevProps.selectedDocumentDetails as any)[f]
        const nextVal = (nextProps.selectedDocumentDetails as any)[f]
        const isEqual =
            typeof prevVal === 'object' && prevVal !== null
                ? JSON.stringify(prevVal) === JSON.stringify(nextVal)
                : prevVal === nextVal
        if (!isEqual) return false
    }

    const keys = relevantFields.map(f => `${nextProps.fieldPrefix}_${f}`)
    const extract = (state?: ValidationState) =>
        keys
            .map(k => {
                const v = state?.[k]
                return v ? `${k}:${v.isError ? 1 : 0}:${v.touched ? 1 : 0}:${v.errorMessage || ''}` : `${k}:0:0:`
            })
            .join('|')

    if (extract(prevProps.validationState) !== extract(nextProps.validationState)) return false

    return true
}

export default React.memo(DocumentDetailsForm, areEqual)
